import React, { useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Globe, Plus, Trash2, Sparkles } from "lucide-react";

const CUSTOM = "__custom__";

export function I18nLanguagesCard({ config, mutate, busy }) {
  const [pick, setPick] = useState("");
  const [custom, setCustom] = useState({ code: "", label: "", flag: "" });

  const add = async () => {
    const body = pick === CUSTOM ? { code: custom.code.trim().toLowerCase(), label: custom.label.trim(), flag: custom.flag.trim() } : { code: pick };
    if (!body.code) return;
    const ok = await mutate(() => api.post("/admin/i18n/languages", body), `Langue ${body.code.toUpperCase()} ajoutée — le site est traduit automatiquement`);
    if (ok) { setPick(""); setCustom({ code: "", label: "", flag: "" }); }
  };

  return (
    <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="i18n-languages-card">
      <div className="font-display font-bold text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-[#002FA7]" /> Langues du site</div>
      <div className="text-xs text-[#495057] mt-1 mb-4 flex items-start gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-[#F59E0B] shrink-0 mt-0.5" />
        <span>Ajoutez une langue : elle apparaît immédiatement dans le sélecteur et <b>tout le contenu est traduit automatiquement par l'IA</b> (cache partagé, relecture possible ci-dessous). Le français reste la langue de référence.</span>
      </div>

      <div className="divide-y divide-[#DEE2E6] border border-[#DEE2E6] mb-4">
        {config.languages.map((l) => (
          <div key={l.code} className={`flex items-center gap-3 px-3 py-2 text-sm ${l.enabled === false ? "opacity-50" : ""}`} data-testid={`i18n-lang-row-${l.code}`}>
            <span className="text-xl leading-none">{l.flag}</span>
            <span className="font-semibold flex-1 truncate">{l.label}</span>
            <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 bg-[#F1F3F5]">{l.code}</span>
            {l.code === "fr" ? (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-[#0A0A0A] text-white px-1.5 py-0.5">Référence</span>
            ) : (
              <>
                <Switch checked={l.enabled !== false} disabled={busy} data-testid={`i18n-lang-toggle-${l.code}`}
                        onCheckedChange={(v) => mutate(() => api.patch(`/admin/i18n/languages/${l.code}`, { enabled: v }), v ? `${l.label} activée` : `${l.label} désactivée`)} />
                <Button size="sm" variant="outline" disabled={busy} data-testid={`i18n-lang-delete-${l.code}`} className="h-7 px-2 text-[#E60000] hover:bg-[#E60000] hover:text-white"
                        onClick={() => { if (window.confirm(`Retirer la langue ${l.label} ?`)) mutate(() => api.delete(`/admin/i18n/languages/${l.code}`), `${l.label} retirée`); }}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#495057]">Ajouter une langue</div>
        <div className="flex gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 h-9 px-2 text-xs border border-[#DEE2E6] bg-white" data-testid="i18n-lang-pick">
            <option value="">Choisir dans le catalogue…</option>
            {config.catalog.languages.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label} ({l.code})</option>)}
            <option value={CUSTOM}>Autre langue (code ISO personnalisé)…</option>
          </select>
          <Button onClick={add} disabled={busy || !pick || (pick === CUSTOM && (!custom.code.trim() || !custom.label.trim()))} data-testid="i18n-lang-add" className="h-9 bg-[#002FA7] text-white">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </div>
        {pick === CUSTOM && (
          <div className="grid grid-cols-3 gap-2" data-testid="i18n-lang-custom">
            <Input value={custom.code} onChange={(e) => setCustom({ ...custom, code: e.target.value })} placeholder="Code (ex. eu)" className="h-9 text-xs" data-testid="i18n-lang-custom-code" />
            <Input value={custom.label} onChange={(e) => setCustom({ ...custom, label: e.target.value })} placeholder="Libellé (ex. Euskara)" className="h-9 text-xs" data-testid="i18n-lang-custom-label" />
            <Input value={custom.flag} onChange={(e) => setCustom({ ...custom, flag: e.target.value })} placeholder="Drapeau (emoji)" className="h-9 text-xs" data-testid="i18n-lang-custom-flag" />
          </div>
        )}
      </div>
    </div>
  );
}
